import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';

import FieldCheckbox from '../fields/fieldCheckbox/FieldCheckbox';
import ChannelPrice from '../../containers/channelUtils/ChannelPrice';
import Tooltip from "../tooltip/Tooltip";
import FieldRadio from "../fields/fieldRadio/FieldRadio";
import FieldSelect from "../fields/fieldSelect/FieldSelect";
import {connect} from "react-redux";

export function calculateOption(basePrice, quantity, option) {
  if (option.quantityIndependent) {
    quantity = 1;
  }

  switch (option.pricing.operation) {
    case '*':
      return basePrice * quantity * parseFloat(option.pricing.modifier);
    case '+':
      return parseFloat(option.pricing.price) * quantity;
    default:
      return 0;
  }
}

export function applyOptions(basePrice, quantity, options) {
  return options
  .map(option => calculateOption(basePrice, quantity, option))
  .reduce((acc, delta) => acc + delta, basePrice * quantity);
}

function OptionValue({operation, priceDelta}) {
  switch (operation) {
    case '+':
    case '*':
      return priceDelta !== 0 && (
      <span className="product__option-price">
          {priceDelta > 0 && '+'}
        {priceDelta < 0 && '-'}
        <ChannelPrice price={Math.abs(priceDelta)}/>
        </span>
      );
    default:
      return false;
  }
}

const widgetTypes = {
  checkbox_list: 'checkbox_list',
  radio_button_list: 'radio_button_list',
  single_choice_dropdown: 'select',
  single_choice_button_group: 'button_group',
}

const mapStateToProps = (({channels}) => (
  {
    channels: channels,
  }
))

const ProductOptionsPicker = ({basePrice, quantity, availableOptions, selectedOptionIds, onChange, row, channels}) => {
  const [showTooltip, setShowTooltip] = useState(null);
  const [showPersonalTooltip, setShowPersonalTooltip] = useState(null);

  useEffect(() => {
    const checkedDefault = availableOptions
        .filter(it => ProductOptionsPicker.supportedOperations.includes(it.pricing.operation))
        .filter(item => item.select);

    const selectedArr = [];
    const seenGroups = new Set();

    checkedDefault.forEach(option => {
      let widgetType
      if (row){
        widgetType = option && option.group && option.group.rankWidgetType || option && option.group && option.group.widgetType || {};
      } else {
        widgetType = option && option.group && option.group.widgetType || {};
      }

      if ((widgetType === 'button_group' || widgetType === 'radio_button_list') && option.select) {
        if (!seenGroups.has(option.group.id)) {
          selectedArr.push(option.id);
          seenGroups.add(option.group.id);
        }
      } else {
        selectedArr.push(option.id);
      }
    });
    onChange(selectedOptionIds.concat(selectedArr))
  }, [availableOptions]);

  function isOptionInvalid(option) {
    if (!option || !option.group || !option.group.widgetType) {
      return true;
    }
  }

  const groupedOptions = availableOptions
    .filter(it => ProductOptionsPicker.supportedOperations.includes(it.pricing.operation))
    .map(it => ({...it, priceDelta: calculateOption(basePrice, quantity, it)}))
    .reduce((acc, option) => {
      const groupName = (option.group && option.group.name) ? option.group.name : '';
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(option);
      return acc;
    }, {});

  const groupKeys = Object.keys(groupedOptions);
  const sortedGroupNames = groupKeys.sort((a, b) => {
    if (a === '') return 1;
    if (b === '') return -1;
    return groupKeys.indexOf(a) - groupKeys.indexOf(b);
  });

  const sortedGroups = Object.entries(groupedOptions).sort((a, b) => {
    const groupNameA = a[0];
    const groupNameB = b[0];

    const hasOptionsA = a[1].length > 0;
    const hasOptionsB = b[1].length > 0;

    if (groupNameA === '' && groupNameB !== '') {
      return 1;
    }
    if (groupNameB === '' && groupNameA !== '') {
      return -1;
    }

    if (hasOptionsA && !hasOptionsB) {
      return -1;
    }
    if (!hasOptionsA && hasOptionsB) {
      return 1;
    }

    const minPositionA = Math.min(...a[1].map(option => option.position));
    const minPositionB = Math.min(...b[1].map(option => option.position));

    return minPositionA - minPositionB;
  });

  const sortedGroupedOptions = sortedGroups.reduce((acc, [groupName, options]) => {
    acc[groupName] = options.sort((a, b) => a.position - b.position);
    return acc;
  }, {});

  const rows = Math.ceil((availableOptions.length + sortedGroupNames.length) / (availableOptions.length < 4 ? 1 : 2));
  const renderOptionType = ((option) => {
    let widgetType;

    if (row) {
      widgetType = option && option.group && option.group.rankWidgetType || option && option.group && option.group.widgetType;
    } else {
      widgetType = option && option.group && option.group.widgetType;
    }

    try {
      if (widgetType === widgetTypes.checkbox_list || isOptionInvalid(option) || !option.group) {
        return (
          <FieldCheckbox
            key={option.id}
            className="field-checkbox--option"
            input={{
              checked: selectedOptionIds.includes(option.id),
              onChange: evt => onChange(
                evt.target.checked
                  ? selectedOptionIds.concat(option.id)
                  : selectedOptionIds.filter(it => it !== option.id)
              )
            }}
          >
            <span className="product__option-text">{option.name}</span>
            {option.tip && (
              <div onMouseLeave={(e) => setShowPersonalTooltip(null)} onMouseEnter={() => setShowPersonalTooltip(option.id)}>
                <p className='product__options-tooltipitem'>?</p>
                <Tooltip content={option.tip} open={option.id === showPersonalTooltip}></Tooltip>
              </div>
            )}
            <OptionValue
              operation={option.pricing.operation}
              priceDelta={option.priceDelta}
              modifier={option.pricing.modifier}
            />
          </FieldCheckbox>
        );
      }

      if (widgetType === widgetTypes.radio_button_list) {
        const myGroupHasSelect = groupedOptions[option.group.name].filter(item => item.select === true);
        return (
          <FieldRadio
            key={option.id}
            className="field-radio--option field-radio--group"
            input={{
              name: option.group.code,
              value: option.id,
              checked: selectedOptionIds.includes(option.id),
              onClick: evt => {
                const myGroupIds = groupedOptions[option.group.name].map(item => item.id);
                const newSelectedId = option.id;
                if (evt.target.checked) {
                  if (!!myGroupHasSelect.length) {
                    if (myGroupIds.includes(newSelectedId)) {
                      const updatedSelectedIds = selectedOptionIds.map(id =>
                          myGroupIds.includes(id) ? newSelectedId : id
                      );

                      if (!updatedSelectedIds.includes(newSelectedId)) {
                        updatedSelectedIds.push(newSelectedId);
                      }
                      onChange(updatedSelectedIds);
                    }
                  } else {
                    const isCurrentlySelected = selectedOptionIds.includes(newSelectedId);

                    if (isCurrentlySelected) {
                      const updatedSelectedIds = selectedOptionIds.filter(id => id !== newSelectedId);
                      onChange(updatedSelectedIds);
                    } else {
                      const updatedSelectedIds = selectedOptionIds.filter(id => !myGroupIds.includes(id));
                      updatedSelectedIds.push(newSelectedId);
                      onChange(updatedSelectedIds);
                    }
                  }
                }
              }
            }}
          >
            <span className="product__option-text">{option.name}</span>
            {option.tip && (
              <div onMouseLeave={(e) => setShowPersonalTooltip(null)}
                 onMouseEnter={() => setShowPersonalTooltip(option.id)}>
                <p className='product__options-tooltipitem'>?</p>
                <Tooltip content={option.tip} open={option.id === showPersonalTooltip}></Tooltip>
              </div>
            )}
            <OptionValue
                operation={option.pricing.operation}
                priceDelta={option.priceDelta}
              modifier={option.pricing.modifier}
            />
          </FieldRadio>
        )
      }

      if (widgetType === widgetTypes.single_choice_button_group) {
        const myGroupHasSelect = groupedOptions[option.group.name].filter(item => item.select === true);
        return (
          <FieldRadio
            key={option.id}
            className="field-radio--option field-radio--button"
            input={{
              name: option.group.code,
              value: option.id,
              checked: selectedOptionIds.includes(option.id),
              onClick: evt => {
                const myGroupIds = groupedOptions[option.group.name].map(item => item.id);
                const newSelectedId = option.id;
                if (evt.target.checked) {
                  if (!!myGroupHasSelect.length) {
                    if (myGroupIds.includes(newSelectedId)) {
                      const updatedSelectedIds = selectedOptionIds.map(id =>
                          myGroupIds.includes(id) ? newSelectedId : id
                      );

                      if (!updatedSelectedIds.includes(newSelectedId)) {
                        updatedSelectedIds.push(newSelectedId);
                      }
                      onChange(updatedSelectedIds);
                    }
                  } else {
                    const isCurrentlySelected = selectedOptionIds.includes(newSelectedId);

                    if (isCurrentlySelected) {
                      const updatedSelectedIds = selectedOptionIds.filter(id => id !== newSelectedId);
                      onChange(updatedSelectedIds);
                    } else {
                      const updatedSelectedIds = selectedOptionIds.filter(id => !myGroupIds.includes(id));
                      updatedSelectedIds.push(newSelectedId);
                      onChange(updatedSelectedIds);
                    }
                  }
                }
              }
            }}
          >
            <span className="product__option-text">{option.name}</span>
            <OptionValue
                operation={option.pricing.operation}
                priceDelta={option.priceDelta}
              modifier={option.pricing.modifier}
            />
          </FieldRadio>
        )
      }
    } catch (error) {
      return null
    }
  });

  return (
    <div style={{gridTemplateRows: `repeat(${rows}, 1fr)`}} className={cx('product__options', row && 'product__options--row')}>
      {sortedGroupNames.map(groupName => {
        const groupOptions = groupedOptions[groupName];
        const groupTip = groupOptions.length > 0 && groupOptions[0].group && groupOptions[0].group.tip
          ? groupOptions[0].group.tip
          : null;
        const selectOptions = groupOptions.filter(option => {
          if (row) {
            return (
              option &&
              option.group &&
              option.group.rankWidgetType === widgetTypes.single_choice_dropdown || option &&
              option.group &&
              option.group.widgetType === widgetTypes.single_choice_dropdown
            )

          } else {
            return (
              option &&
              option.group &&
              option.group.widgetType === widgetTypes.single_choice_dropdown
            )
          }
        }
        );

        const selectHasSelected = selectOptions.filter(option => option.select === true).length;
        const firstSelectSelectedOption = selectOptions.find(option => option.select === true);
        const firstSelectSelectedOptionId = firstSelectSelectedOption ? firstSelectSelectedOption.id : null;

        return (
          <React.Fragment key={groupName}>
            <div onMouseLeave={() => setShowTooltip(null)} onMouseEnter={() => setShowTooltip(groupName)} className='product__options-wrapper'>
              {groupName ? groupName : (Object.keys(sortedGroupedOptions).length === 1 && groupName === '') ? null : <div className='product__options-divider'></div>}
              {groupTip && (
                <div>
                  <p className='product__options-tooltipitem'>?</p>
                  <Tooltip content={groupTip} open={showTooltip === groupName}></Tooltip>
                </div>
              )}
            </div>
            {groupOptions.map(option => (
              renderOptionType(option)
            ))}
            {selectOptions.length > 0 && (
              <FieldSelect onChange={(evt) => {
                const selectedValue = Number(evt.target.value);
                const groupIDs = selectOptions.filter(option => option.group.name === groupName).map((item) => item.id);

                let newSelectedOptionIds;

                if (selectedValue === 0) {
                  newSelectedOptionIds = selectedOptionIds.filter(id => !groupIDs.includes(id));
                } else {
                  const intersection = selectedOptionIds.filter(id => groupIDs.includes(id));

                  if (intersection.length > 0) {
                    newSelectedOptionIds = selectedOptionIds.map(id => (intersection.includes(id) ? selectedValue : id));
                  } else {
                    newSelectedOptionIds = [...selectedOptionIds, selectedValue];
                  }
                }

                onChange(newSelectedOptionIds);
              }}>
              {!selectHasSelected && <option value="0">Select option</option>}
              {selectOptions.map(option => (
               <option key={option.id} value={option.id} selected={firstSelectSelectedOptionId === option.id}>
                 {option.name} {option.pricing.price !== 0 ?
                   `${option.priceDelta > 0 ? '+' : '-'}
                 ${channels.availableChannels[channels.activeChannel].baseCurrency.symbol}${ChannelPrice.formatPrice(channels.availableChannels[channels.activeChannel].baseCurrency, Math.abs(option.priceDelta))}` : null}
               </option>
              ))}
              </FieldSelect>
            )}
      </React.Fragment>
    );
  })}
    </div>
  );
}

ProductOptionsPicker.propTypes = {
  basePrice: PropTypes.number.isRequired,
  quantity: PropTypes.number.isRequired,
  availableOptions: PropTypes.array.isRequired,
  selectedOptionIds: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  row: PropTypes.bool
};


ProductOptionsPicker.supportedOperations = ['+', '*'];

export default connect(mapStateToProps)(ProductOptionsPicker)



// WEBPACK FOOTER //
// ./components/productPurchase/ProductOptionsPicker.js